import React, { PureComponent } from 'react';
import {
  Form,
  ImageInput,
  TextInput,
  CheckboxGroupInput,
  BooleanInput,
  NumberInput,
  required,
  useNotify,
} from 'react-admin';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import dataProvider from '../../../providers/dataProvider';
import uploadPicture from '../../../utils/uploadPicture';
import ImagePickerButton from '../../../components/ImagePickerButton/ImagePickerButton';
import ImgField from '../../../components/ImgField/ImgField';
import normalizeDurationOptions from '../normalizeDurationOptions';

// Define action type constants for react-admin 5.x compatibility
const CREATE = 'CREATE';
const UPDATE = 'UPDATE';

const durationChoices = [
  { id: '5', name: '5' },
  { id: '10', name: '10' },
  { id: '15', name: '15' },
  { id: '20', name: '20' },
  { id: '30', name: '30' },
  { id: '35', name: '35' },
  { id: '45', name: '45' },
  { id: '60', name: '60' },
  { id: '75', name: '75' },
];

class SessionModal extends PureComponent {
  uploadImg = (img) => uploadPicture(img);

  notifyError = (error) => {
    this.props.notify(error?.message || error || 'Failed to save session', { type: 'error' });
  };

  create = (event) => {
    return Promise.all([this.uploadImg(event.picture)])
      .then((res) => {
        const data = {
          ...event,
          picture: res[0].data.fileName,
          programId: this.props.programId,
          durationOptions: normalizeDurationOptions(event.durationOptions),
          isBasic: event.isBasic === undefined ? false : event.isBasic,
          challengeOrder: event.challengeOrder ?? null,
        };
        return dataProvider(CREATE, 'sessions', { data });
      })
      .then(() => {
        this.props.handleClose();
        this.props.refreshList();
      })
      .catch(this.notifyError);
  };

  update = (event) => {
    return Promise.all([this.uploadImg(event.picture)])
      .then((res) => {
        const data = {
          ...event,
          picture: res[0].data.fileName,
          programId: this.props.programId,
          durationOptions: normalizeDurationOptions(event.durationOptions),
        };
        return dataProvider(UPDATE, 'sessions', { id: data.id, data });
      })
      .then(() => {
        this.props.handleClose();
        this.props.refreshList();
      })
      .catch(this.notifyError);
  };

  handleSubmit = (event) => {
    if (event.id) {
      return this.update(event);
    }
    return this.create(event);
  };

  render() {
    const { initialValues } = this.props;
    return (
      <Dialog
        maxWidth="md"
        open={this.props.isOpen}
        onClose={this.props.handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Session</DialogTitle>
        {/*
          The dialog is portalled, but React context still flows from the program edit
          form this modal is rendered inside. Without its own Form the inputs below bind
          to the program's form: they prefill with the program's values, overwrite them
          as you type, and submit nothing. stopPropagation keeps this form's submit from
          bubbling up and saving the program too.
        */}
        <div onSubmit={(event) => event.stopPropagation()}>
          <Form record={initialValues || {}} onSubmit={this.handleSubmit}>
            <DialogContent>
              <ImageInput
                source="picture"
                label="Session Photo"
                accept={{ 'image/*': [] }}
                format={(v) => (typeof v === 'string' ? { picture: v } : v)}
                validate={required((_) => 'ra.validation.required')}
              >
                <ImgField width="100" height="100" source="picture" />
              </ImageInput>
              <ImagePickerButton source="picture" label={false} />
              <TextInput source="name" label="Session name" validate={required()} />
              <div>Duration Options (in minutes)</div>
              <CheckboxGroupInput
                source="durationOptions"
                validate={required()}
                disabled={!!(initialValues && initialValues.id)}
                format={(v) => {
                  if (v && !Array.isArray(v)) {
                    return Object.keys(v);
                  }
                  return v;
                }}
                label="These are approximate session durations. Final duration will vary depending on the exercises added on each session"
                choices={durationChoices}
              />
              <TextInput source="description" label="Description" validate={required()} />
              <BooleanInput label="Basic Level" source="isBasic" />
              <Typography component="div">
                Mark this session as basic level if you want this to be the session customers get
                when they indicate they are feeling more tire/sore/stiff than usual at the end of
                the prep phase.
              </Typography>
              <NumberInput
                label="Challenge Order"
                source="challengeOrder"
                min={1}
                step={1}
                parse={(v) => (v === '' || v == null || Number.isNaN(v) ? null : v)}
              />
              <Typography component="div">
                Set 1, 2, or 3 to make this a Challenge Session. Leave empty for a regular session.
                A Challenge Session must still be placed in each program track it belongs to
                (Program → Tracks); the normal schedule skips it and it is offered dynamically
                instead: challenge 1 in the first half of the track, challenge 2 from the midpoint,
                challenge 3 in the final quarter. It is only offered for workout durations it
                supports, so cover every duration the track&apos;s regular sessions offer.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.props.handleClose}>Cancel</Button>
              <Button variant="contained" type="submit" color="primary">
                Save
              </Button>
            </DialogActions>
          </Form>
        </div>
      </Dialog>
    );
  }
}

const SessionModalWithNotify = (props) => {
  const notify = useNotify();
  return <SessionModal {...props} notify={notify} />;
};

export default SessionModalWithNotify;
